import React from 'react';
import { motion } from 'motion/react';
import { Gavel, ArrowLeft, ShieldAlert, Scale, Clock, Globe, Briefcase, Zap, Info, Landmark, Mail, ExternalLink, ShieldCheck } from 'lucide-react';

interface TermsOfServiceProps {
  onBack?: () => void;
}

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {
  return (
    <div className="bg-[#05050a] min-h-screen text-slate-100 font-sans selection:bg-indigo-500/30 overflow-y-auto custom-scrollbar relative z-10">
      {/* Background Decor */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/10 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 blur-[120px] rounded-full" />
        <div className="absolute top-[20%] left-[10%] w-[1px] h-[60%] bg-gradient-to-b from-transparent via-purple-500/10 to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-6 py-20 relative z-10">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={onBack || (() => window.history.back())}
          className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors mb-12 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-bold uppercase tracking-wider text-sm">Back to Experience</span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative inline-block mb-16">
            <div className="flex items-center gap-6">
              <div className="p-4 bg-purple-600/10 rounded-2xl border border-purple-500/20 relative group">
                <div className="absolute -inset-1 bg-purple-500/20 blur-xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <Gavel className="w-12 h-12 text-purple-400 relative z-10" />
              </div>
              <div>
                <h1 className="text-5xl sm:text-7xl font-black tracking-tighter text-white uppercase italic drop-shadow-[0_0_20px_rgba(255,255,255,0.1)]">
                  Terms <span className="text-purple-400">Of Service</span>
                </h1>
                <div className="flex items-center gap-2 mt-3">
                  <div className="h-[2px] w-12 bg-purple-500 rounded-full" />
                  <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Legal Governance Framework • August 2026</p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Details Metadata Card */}
          <div className="mb-12 p-6 bg-slate-900/60 border border-purple-500/20 rounded-3xl backdrop-blur-xl grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs font-mono">
            <div>
              <span className="text-slate-500 uppercase tracking-wider block mb-1">Effective Date</span>
              <span className="text-purple-300 font-bold">August 2026</span>
            </div>
            <div>
              <span className="text-slate-500 uppercase tracking-wider block mb-1">Entity</span>
              <span className="text-white font-bold">Professor X AI Labs</span>
            </div>
            <div>
              <span className="text-slate-500 uppercase tracking-wider block mb-1">Contact Email</span>
              <a href="mailto:professorxai108@gmail.com" className="text-purple-400 hover:underline font-bold">professorxai108@gmail.com</a>
            </div>
            <div>
              <span className="text-slate-500 uppercase tracking-wider block mb-1">Application Homepage</span>
              <a href="https://professorxai.info/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline font-bold flex items-center gap-1">
                professorxai.info <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          <div className="space-y-16 mt-8 text-slate-400 leading-relaxed max-w-4xl">
            {/* 01. Acceptance of Terms & Algorithmic Framework */}
            <section className="relative group">
              <div className="absolute -inset-6 bg-gradient-to-r from-purple-500/5 to-indigo-500/5 rounded-[3rem] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.02] border border-white/5 p-12 rounded-[2.5rem] backdrop-blur-2xl">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                    <Globe className="w-6 h-6 text-purple-400" />
                  </div>
                  <h2 className="text-3xl font-black text-white uppercase tracking-tight italic">01. Acceptance of Terms & Algorithmic Framework</h2>
                </div>
                <p className="text-xl font-medium text-slate-300">
                  By initializing an account session or interacting with Professor X (<code className="font-mono text-purple-300 text-lg">https://professorxai.info/</code>), you enter into a legally binding contract with Professor X AI Labs. This framework governs your access to our visual tools, algorithmic pedagogical explanations, and AI integrations. If you do not agree to every clause herein, you must immediately terminate your interaction with the platform.
                </p>
              </div>
            </section>

            {/* 02. Usage Parameters & Intellectual Property */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <Briefcase className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">02. Usage Parameters & Intellectual Property</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all group/card">
                  <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2 text-sm">
                    <Zap className="w-4 h-4 text-purple-400" /> Revocable Educational License
                  </h3>
                  <p className="text-sm">You are granted a limited, non-exclusive, non-transferable, revocable license to use Professor X for personal, non-commercial educational advancement.</p>
                </div>
                <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all group/card">
                  <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2 text-sm">
                    <ShieldAlert className="w-4 h-4 text-purple-400" /> Commercial Restrictions
                  </h3>
                  <p className="text-sm">Systematic scraping, commercial redistribution, resale, or sublicensing of our platform outputs or user interface assets is strictly prohibited.</p>
                </div>
                <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all group/card">
                  <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2 text-sm">
                    <Scale className="w-4 h-4 text-purple-400" /> Academic Integrity Policy
                  </h3>
                  <p className="text-sm">Professor X is engineered as a collaborative visual learning aid. You agree <strong>not</strong> to misuse the service to engage in academic dishonesty, violate institutional codes of conduct, cheat on proctored examinations, or generate fraudulent academic submissions.</p>
                </div>
              </div>
            </section>

            {/* 03. Google API & OAuth Usage Terms */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">03. Google API & OAuth Usage Terms</h2>
              </div>
              <p className="text-slate-300">
                By logging in via Google OAuth to use Professor X, you acknowledge and agree that:
              </p>
              <div className="space-y-4">
                <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-300 font-bold flex items-center justify-center shrink-0 text-sm">1</span>
                  <p className="text-sm pt-1">You grant Professor X authorization to issue Generative Language API requests using the temporary OAuth tokens generated during your session.</p>
                </div>
                <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-300 font-bold flex items-center justify-center shrink-0 text-sm">2</span>
                  <p className="text-sm pt-1">
                    Your interactions with generative AI endpoints are subject to{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline font-bold inline-flex items-center gap-1">
                      Google's Terms of Service <ExternalLink className="w-3 h-3" />
                    </a>{' '}
                    and{' '}
                    <a href="https://policies.google.com/terms/generative-ai" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline font-bold inline-flex items-center gap-1">
                      Generative AI Additional Terms of Service <ExternalLink className="w-3 h-3" />
                    </a>.
                  </p>
                </div>
                <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-300 font-bold flex items-center justify-center shrink-0 text-sm">3</span>
                  <p className="text-sm pt-1">You will not attempt to exploit OAuth tokens, forge client credentials, or bypass Google API quota limits.</p>
                </div>
              </div>
            </section>

            {/* 04. Prohibited Conduct */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <ShieldAlert className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">04. Prohibited Conduct</h2>
              </div>
              <p className="text-slate-300">When accessing Professor X, you explicitly agree <strong>not</strong> to:</p>
              <ul className="grid sm:grid-cols-2 gap-4 text-sm">
                <li className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-purple-400 mt-2 shrink-0" />
                  <span>Decompile, reverse-engineer, or attempt to extract source code or underlying parameters from the platform or its API endpoints.</span>
                </li>
                <li className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-purple-400 mt-2 shrink-0" />
                  <span>Upload malformed scripts, viruses, or malicious payloads designed to disrupt service availability.</span>
                </li>
                <li className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-purple-400 mt-2 shrink-0" />
                  <span>Automate query submissions via unapproved web scrapers, bots, or automated extraction tools.</span>
                </li>
                <li className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-purple-400 mt-2 shrink-0" />
                  <span>Use the platform to generate unlawful, harassing, defamatory, or dangerous content.</span>
                </li>
              </ul>
            </section>

            {/* 05. Risk Allocations & Disclaimers */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <Info className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">05. Risk Allocations & Disclaimers</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all">
                  <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-purple-400" /> Accuracy Disclaimer
                  </h3>
                  <p className="text-sm">Generative AI models operate probabilistically and may occasionally generate inaccurate mathematical, scientific, or factual information ("hallucinations"). Instructional content is provided on an <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong> basis without guarantees of academic performance, certification pass rates, or specific test scores.</p>
                </div>
                <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all">
                  <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-400" /> Service Availability & Latency
                  </h3>
                  <p className="text-sm">Platform functionality relies on third-party cloud infrastructure, including Google Cloud and the Generative Language API. Professor X AI Labs is not liable for service interruptions, latency, rate limits, or outages originating from external network nodes.</p>
                </div>
              </div>
            </section>

            {/* 06. Limitation of Liability */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <Scale className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">06. Limitation of Liability</h2>
              </div>
              <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all">
                <p className="text-slate-300 text-sm leading-relaxed">
                  To the maximum extent permitted by applicable law, in no event shall Professor X AI Labs, its developers, founders, or affiliates be liable for any indirect, incidental, punitive, special, or consequential damages arising out of or in connection with your use or inability to use the platform.
                </p>
              </div>
            </section>

            {/* 07. Statutory Framework & Dispute Resolution */}
            <section className="space-y-8">
              <div className="flex items-center gap-3">
                <Landmark className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">07. Statutory Framework & Dispute Resolution</h2>
              </div>
              <div className="bg-purple-900/10 border border-purple-500/20 p-10 rounded-[2.5rem] relative overflow-hidden group">
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 blur-3xl rounded-full -translate-x-16 translate-y-16" />
                <div className="relative pl-8 space-y-8 border-l-2 border-purple-500/30">
                  <div className="space-y-2">
                    <h4 className="text-white font-black uppercase tracking-tighter text-sm">Individual Binding Arbitration</h4>
                    <p className="text-sm text-slate-300">You agree that any dispute, claim, or controversy arising out of these Terms shall be settled through binding individual arbitration under commercial arbitration standards, rather than in court.</p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-white font-black uppercase tracking-tighter text-sm">Class Action Waiver</h4>
                    <p className="text-sm text-slate-300">You explicitly waive the right to initiate, join, or participate as a plaintiff or class member in any class action, collective lawsuit, or representative proceeding against Professor X AI Labs.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* 08. Contact Information */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">08. Contact Information</h2>
              </div>
              <p className="text-slate-300">
                If you have legal questions, security inquiries, or privacy requests regarding these documents, contact us at:
              </p>
              <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 flex flex-col sm:flex-row gap-6 text-sm">
                <div>
                  <span className="text-slate-500 block mb-1">📧 Email</span>
                  <a href="mailto:professorxai108@gmail.com" className="text-purple-400 font-bold hover:underline font-mono">professorxai108@gmail.com</a>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">🌐 Website</span>
                  <a href="https://professorxai.info/" target="_blank" rel="noopener noreferrer" className="text-purple-400 font-bold hover:underline font-mono flex items-center gap-1">
                    https://professorxai.info/ <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </section>

            <section className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center gap-8 text-slate-400">
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center gap-3 mb-2 justify-center md:justify-start">
                  <Mail className="w-5 h-5 text-purple-400" />
                  <h3 className="text-xl font-bold text-white uppercase italic">Legal Support</h3>
                </div>
                <p className="text-sm">Inquiries regarding these terms? Contact our Legal Affairs department:</p>
                <a href="mailto:professorxai108@gmail.com" className="text-purple-400 font-bold hover:underline font-mono">professorxai108@gmail.com</a>
              </div>
              <button 
                onClick={onBack || (() => window.history.back())}
                className="px-16 py-5 bg-purple-600 hover:bg-purple-500 text-white rounded-2xl font-black uppercase tracking-[0.2em] transition-all shadow-2xl shadow-purple-500/20 hover:scale-105 active:scale-95 text-sm"
              >
                Accept Legal Terms
              </button>
            </section>
          </div>
        </motion.div>

        <footer className="mt-32 pt-12 border-t border-white/5 text-center">
          <p className="text-slate-600 text-xs font-bold uppercase tracking-widest leading-loose">
             &copy; 2026 Professor X AI Labs • Global Operations<br/>
             Statutory Governance • Intellectual Rights Preserved
          </p>
        </footer>
      </div>
    </div>
  );
};
